import { Component, OnInit, ViewChild, Input, Output, EventEmitter, AfterViewInit, forwardRef, NgZone, OnChanges, SimpleChange, SimpleChanges } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NumericEditor } from './numeric-editor.component';
import { SpreadsheetConsts } from '../spreadsheet-consts';
import { BaseTranslation } from 'src/core/translation/model/translation.base';
import { TranslateService } from '@ngx-translate/core';
import { LanguageId, Language } from 'src/core/utility/enums/enums';
import { SpreedSheetGridService } from '../spreadsheet-grid.service';

@Component({
  selector: 'spreadsheet-grid',
  templateUrl: './spreadsheet-grid.component.html',
  styleUrls: ['./spreadsheet-grid.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SpreadsheetGridComponent),
      multi: true
    }
  ]
})
export class SpreadsheetGridComponent extends BaseTranslation implements OnInit, ControlValueAccessor {

  private gridApi;
  private gridColumnApi;
  frameworkComponents;
  colDefs = [];
  rowData: [];
  totalFieldName: string;
  disabled: string;
  enableRtl: boolean;
  totalTranslation: string;
  overlayLoadingTemplate;
  focusedCell;
  updatingCell;
  //#region View Childs

  @ViewChild(AgGridAngular) agGrid: AgGridAngular;

  //#endregion

  //#region Inputs

  @Input('rowsData') rowsData: any[];

  @Input('list') list;

  @Input('headers') headers;

  @Input('config') config;

  @Input('useInnerGridWrapper') useInnerGridWrapper: boolean = false;

  //#endregion

  //#region Outputs

  @Output('cellClicked') cellClicked: EventEmitter<any> = new EventEmitter();

  @Output('cellValueChanged') cellValueChanged: EventEmitter<any> = new EventEmitter();

  @Output('deleteCellClicked') deleteCellClicked: EventEmitter<any> = new EventEmitter();

  @Input('subscribeInnerGridData') subscribeInnerGridData;

  @Input('showLoader') showLoader: Boolean = false;
  //#endregion

  constructor(ngZone: NgZone, private translateService: TranslateService, private gridService: SpreedSheetGridService) {
    super(translateService, ngZone);

  }

  //#region LifeCycleHooks
  ngOnInit() {
    this.subscribeIfInnerGrid();

    this.frameworkComponents = {
      numericEditor: NumericEditor
    };

    this.overlayLoadingTemplate = '<div class="ag-overlay-loading-center"><span class="spinner"></span></div>';

    this.translateService.get('common.Total').subscribe((trans) => {
      this.totalTranslation = trans;
      this.initColDefs();
      this.initColRows(this.rowsData);
      if (_spPageContextInfo.currentLanguage == Language.ar) {
        this.enableRtl = true;
      } else {
        this.enableRtl = false;
      }
    });
  }
  subscribeIfInnerGrid() {
    if (this.subscribeInnerGridData) {
      this.gridService.refreshGridSource$.subscribe(data => {

        this.gridApi.rowRenderer.releaseLockOnRefresh();
        this.initColRows(data);
        this.gridApi.setRowData(this.rowsData);
        this.gridApi.refreshCells({ force: true });
        this.gridApi.rowRenderer.releaseLockOnRefresh();
      })
    }
  }


  //#endregion

  initColDefs = () => {
    this.colDefs = [];

    if (this.config.includeOptionsColumn) {
      this.colDefs.push(
        { headerName: '', field: SpreadsheetConsts.options, lockPosition: true, cellRenderer: this.optionsCellRendererFunc },
      );
    }

    for (let i = 0; i < this.headers.length; i++) {
      let column: any = {
        headerName: this.headers[i].headerName,
        field: this.headers[i].field,
        headerTooltip: this.headers[i].headerName,
        lockPosition: true,
        cellStyle: { 'white-space': 'normal' },
        autoHeight: true,
        comment: null,
        width: 110
        // suppressColumnVirtualisation: true
      };

      if (this.headers[i].hasOwnProperty('editable')) {
        column.editable = this.headers[i].editable;
      } else {
        column.editable = this.editableFunc;
      }

      if (this.headers[i].hasOwnProperty('colId')) {
        column.colId = this.headers[i].colId;
      }

      if (this.headers[i].hasOwnProperty('customCellRenderer')) {
        this.disabled = this.headers[i].customCellRenderer;
      }

      column.cellRenderer = this.customCellRendererFunc;


      if (this.headers[i].hasOwnProperty('cellEditor')) {
        column.cellEditor = this.headers[i].cellEditor;
      }

      if (this.headers[i].hasOwnProperty('valueGetter')) {
        column.valueGetter = this.headers[i].valueGetter;
      }

      if (this.headers[i].hasOwnProperty('comment')) {
        column.comment = this.headers[i].comment;
      }

      this.colDefs.push(column);
    }

    if (this.config.includeTotalColumn) {
      this.totalFieldName = this.config.totalPropertyName;
      this.colDefs.push(
        {
          headerName: this.totalTranslation, field: 'total', lockPosition: true, editable: false,
          cellRenderer: this.customCellRendererFunc, valueGetter: this.totalValueGetter
        },
      );
    }

    if (this.config.includeDeleteColumn) {
      this.colDefs.push(
        { headerName: '', deleteCell: true, field: "del", lockPosition: true, cellRenderer: this.deleteCellRendererFunc, width: 40 },
      );
    }

  }

  public initColRows = (rows) => {
    this.rowsData = rows;
    if (this.config.includeTotalRow) {
      let totalRow = {};

      this.rowsData = this.rowsData.filter(r => r.row != this.totalTranslation);
      totalRow = { row: this.totalTranslation };

      for (let i = 1; i < this.headers.length; i++) {
        totalRow[this.headers[i].field] = 0;
      }

      for (let i = 1; i < this.headers.length; i++) {
        let total = 0;
        for (let j = 0; j < this.rowsData.length; j++) {
          total += +this.rowsData[j][this.headers[i].field];
        }
        totalRow[this.headers[i].field] = total;
      }

      this.rowsData.push(totalRow);

      this.rowsData = [...this.rowsData];
    }

  }




  //#region Grid Events

  public onCellClicked(event) {
    if (event.colDef.deleteCell) {
      this.deleteCellClicked.emit(event);
    }
    else {
      this.cellClicked.emit(event);
    }
  }

  onCellValueChanged(event) {
    if (event.newValue != event.oldValue) {
      this.updatingCell = true;
      this.focusedCell = this.gridApi.getFocusedCell();
      if (this.showLoader) {
        this.gridApi.showLoadingOverlay();
        this.gridApi.clearFocusedCell();
      }
      if (event.newValue == "")
        event.newValue = 0;
      this.cellValueChanged.emit(event);
    }
  }

  refreshGrid(rows, _list: any) {
    if (_list) {
      this.list = _list
    }
    this.gridApi.rowRenderer.releaseLockOnRefresh();
    if (this.showLoader)
      this.gridApi.clearFocusedCell()
    this.initColRows(rows);
    this.gridApi.setRowData(this.rowsData);
    this.gridApi.refreshCells({ force: true });
    this.updatingCell = false;
    this.onBtStartEditing(this.focusedCell);
    this.gridApi.rowRenderer.releaseLockOnRefresh();
    this.gridApi.hideOverlay();
  }

  onBtStartEditing(focusedCell) {
    if (focusedCell) {
      this.gridApi.setFocusedCell(focusedCell.rowIndex, focusedCell.column, focusedCell.rowPinned, true);
      this.gridApi.startEditingCell({
        rowIndex: focusedCell.rowIndex,
        colKey: focusedCell.column,
        rowPinned: focusedCell.rowPinned
      });
    }
  }

  tabToNextCell = (params) => {
    this.focusedCell = params.nextCellPosition;
    this.onBtStartEditing(params.nextCellPosition);
    if (params.nextCellPosition)
      return { rowIndex: params.nextCellPosition.rowIndex, column: params.nextCellPosition.column, floating: params.nextCellPosition.floating };
  }


  onGridSizeChanged($event) {
    if (!this.useInnerGridWrapper) {
      let wrapperWidth = document.getElementById("grid-wrapper").offsetWidth;
      if (this.useInnerGridWrapper)
        wrapperWidth = (document.querySelectorAll("#grid-wrapper")[1] as any).offsetWidth;

      let totalColsWidth = 0;
      let allColumns = $event.columnApi.getAllColumns();
      for (let i = 0; i < allColumns.length; i++) {
        let column = allColumns[i];
        totalColsWidth += column.actualWidth;
      }
      if (totalColsWidth < wrapperWidth) {
        $event.api.sizeColumnsToFit();
      } else {
        // $event.columnApi.autoSizeColumns();
      }
    } else {
      $event.api.sizeColumnsToFit();
    }
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;

    params.api.setRowData(this.rowsData);
    params.api.resetRowHeights();
  }

  // getRowHeightgetRowHeight(params) {
  //   console.log('getRowHeightgetRowHeight', params);
  //   return 28 * (Math.floor(params.data.name.length / 60) + 1);
  // }

  //#endregion

  //#region ControlValueAccessorMethods

  // Function to call when the data changes.
  onChange = (people: any[]) => { };

  // Function to call when the grid is touched.
  onTouched = () => { };

  get value(): any[] {
    return this.rowsData;
  }

  // Allows Angular to update the model.
  // Update the model and changes needed for the view here.
  writeValue(rows: any[]): void {
    this.rowsData = rows;
    this.onChange(this.value)
  }

  // Allows Angular to register a function to call when the model changes.
  // Save the function as a property to call later here.
  registerOnChange(fn: (rows: any[]) => void): void {
    this.onChange = fn;
  }

  // Allows Angular to register a function to call when the input has been touched.
  // Save the function as a property to call later here.
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }


  //#endregion

  //#region DefaultMethod

  editableFunc = (params) => {

    var column = this.getSelectedCell(params.node.rowIndex, params.column.colId);
    if (this.updatingCell)
      return false;
    if (column && eval(this.disabled)) {
      return false;
    }
    else if (column && column.numberRecords == 1 && !this.config.defaultSecondGrid) {
      return true;
    }
    else {
      return false;
    }
  }

  customCellRendererFunc = (params): string => {
    var column = this.getSelectedCell(params.rowIndex, params.column.colId);
    let cellContent: string = '';
    let toolTipText = '';
    if (column && column.data && column.data.length && column.data.length == 1 && column.data[0].comment) {
      toolTipText = column.data[0].comment;
    } else if (params.colDef && params.colDef.comment) {
      toolTipText = params.colDef.comment;
    } else if (params.data.comment) {
      toolTipText = params.data.comment;
    }




    if ((!column && !params.colDef.editable) || (!column && typeof (params.colDef.editable) == "function" && !params.colDef.editable(params)) || (column && eval(this.disabled))) {
      cellContent += `<div title="${toolTipText}" class="disabled">${params.value}</div>`;
      params.eGridCell.onclick = function (event) { event.stopPropagation(); };
    }
    else if (column && column.numberRecords == 0)
      cellContent += `<div title="${toolTipText}" data-btn="button">${params.value}<span class="icon-add"></span></div>`;
    else if ((column && column.numberRecords > 1 || (this.config.defaultSecondGrid && column && column.numberRecords > 0)))
      cellContent += `<div title="${toolTipText}" data-btn="button">${params.value}<span class="icon-share-2"></span></div>`;
    else if (column && column.numberRecords == 1 || (!column && params.colDef.editable))
      cellContent += `<div title="${toolTipText}">${params.value}</div>`;
    else {
      cellContent += `<div title="${toolTipText}">${params.value}</div>`;
      params.eGridCell.onclick = function (event) { event.stopPropagation(); };
    }
    return cellContent;
  }

  optionsCellRendererFunc = (params) => {

    let cellContent: string = '';
    var column = this.getSelectedCell(params.rowIndex, params.column.colId);

    if (column && column.numberRecords == 0)
      cellContent += `<div data-btn="button"><span class="icon-add"></span></div>`;

    else {
      cellContent += `<div></div>`;
    }

    return cellContent;

  }

  getSelectedCell(rowIndex, columnIndex): any {

    if (this.list && this.list[rowIndex])
      return this.list[rowIndex].columns.find(x => x.id == columnIndex);
  }

  totalValueGetter = (params) => {
    let total: number = 0;
    for (let key of Object.keys(params.data)) {
      if (key.startsWith(this.totalFieldName)) {
        total += +params.data[key];
      }
      params.data.total = total;
    }
    return params.data.total;
  }

  deleteCellRendererFunc = (params) => {

    //var cell = this.getSelectedCell(params.rowIndex, params.column.colId);
    return `<div class="justify-content-center" data-btn="button"><span class="icon-rubbish-bin"></span></div>`;

  }

  //#endregion

}
